/**
 * Bezier curve mathematics — evaluation, reference frames, handle
 * recomputation.
 *
 * The numerical core (Bezier eval, RMF, handle constraint enforcement,
 * subdivision) runs in Rust via `../wasm/curveMath.ts`. A handful of
 * user-event helpers (creating a default curve, switching handle types,
 * one-shot aligned enforcement) stay in TS because they fire only on
 * explicit user actions — the TS↔WASM marshalling would cost more than
 * the math itself.
 */

import type { CurveData, CurveHandleType, CurvePoint } from "./types.ts";

export type { CurveEvaluation, CurveFrame, Vec3 } from "../wasm/curveMath.ts";
export {
  computeRMF,
  enforceHandleConstraints,
  enforceHandleConstraintsBatch,
  evalBezierSegment,
  evalBezierTangent,
  evaluateCurve,
  subdivideCurveSegment,
} from "../wasm/curveMath.ts";

// ---------------------------------------------------------------------------
// TS-only helpers — fire on explicit user events, not per-frame.
// ---------------------------------------------------------------------------

type V3 = [number, number, number];

function v3Sub(a: V3, b: V3): V3 {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}
function v3Add(a: V3, b: V3): V3 {
  return [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
}
function v3Scale(a: V3, s: number): V3 {
  return [a[0] * s, a[1] * s, a[2] * s];
}
function v3Len(a: V3): number {
  return Math.sqrt(a[0] * a[0] + a[1] * a[1] + a[2] * a[2]);
}
function v3Dist(a: V3, b: V3): number {
  return v3Len(v3Sub(a, b));
}
function v3Normalize(a: V3): V3 {
  const len = v3Len(a);
  if (len < 1e-12) return [0, 0, 1];
  return [a[0] / len, a[1] / len, a[2] / len];
}

function pointTangentDirection(
  pt: CurvePoint,
  prev: CurvePoint | null,
  next: CurvePoint | null,
): V3 | null {
  if (prev && next) return v3Normalize(v3Sub(next.co, prev.co));
  if (next) return v3Normalize(v3Sub(next.co, pt.co));
  if (prev) return v3Normalize(v3Sub(pt.co, prev.co));
  return null;
}

function handleDistance(
  pt: CurvePoint,
  prev: CurvePoint | null,
  next: CurvePoint | null,
  side: "left" | "right",
): number {
  const neighbor = side === "left" ? (prev ?? next) : (next ?? prev);
  return neighbor ? v3Dist(pt.co, neighbor.co) / 3 : 0;
}

function setHandleAlongTangent(
  pt: CurvePoint,
  tangent: V3,
  side: "left" | "right",
  distance: number,
): void {
  const sign = side === "left" ? -1 : 1;
  const handle = v3Add(pt.co, v3Scale(tangent, sign * distance));
  if (side === "left") pt.handleLeft = handle;
  else pt.handleRight = handle;
}

function recomputeOneAutoHandle(
  pt: CurvePoint,
  prev: CurvePoint | null,
  next: CurvePoint | null,
  side: "left" | "right",
): void {
  const tangent = pointTangentDirection(pt, prev, next);
  if (!tangent) return;
  const dist = handleDistance(pt, prev, next, side);
  if (dist <= 1e-10) return;
  setHandleAlongTangent(pt, tangent, side, dist);
}

/**
 * Recompute auto handles for all points in the curve. Fires on curve creation
 * and when the user changes handle type to "auto" via the UI.
 */
export function recomputeAutoHandles(
  points: CurvePoint[],
  cyclic: boolean,
): void {
  const n = points.length;
  for (let i = 0; i < n; i++) {
    const pt = points[i];
    const hasPrev = cyclic || i > 0;
    const hasNext = cyclic || i < n - 1;
    const prev = hasPrev ? points[(i - 1 + n) % n] : null;
    const next = hasNext ? points[(i + 1) % n] : null;

    if (pt.handleTypeLeft === "auto")
      recomputeOneAutoHandle(pt, prev, next, "left");
    if (pt.handleTypeRight === "auto")
      recomputeOneAutoHandle(pt, prev, next, "right");
  }
}

/** Recompute a single handle to point at its neighbor at 1/3 distance. */
export function recomputeVectorHandle(
  pt: CurvePoint,
  prev: CurvePoint | null,
  next: CurvePoint | null,
  side: "left" | "right",
): void {
  const neighbor = side === "left" ? prev : next;
  if (!neighbor) return;
  const dir = v3Sub(neighbor.co, pt.co);
  if (side === "left") pt.handleLeft = v3Add(pt.co, v3Scale(dir, 1 / 3));
  else pt.handleRight = v3Add(pt.co, v3Scale(dir, 1 / 3));
}

function recomputeAlignedHandles(points: CurvePoint[], cyclic: boolean): void {
  const n = points.length;
  for (let i = 0; i < n; i++) {
    const pt = points[i];
    const prev = cyclic || i > 0 ? points[(i - 1 + n) % n] : null;
    const next = cyclic || i < n - 1 ? points[(i + 1) % n] : null;
    const tangent = pointTangentDirection(pt, prev, next);
    if (!tangent) continue;

    const leftDist = handleDistance(pt, prev, next, "left");
    const rightDist = handleDistance(pt, prev, next, "right");
    if (leftDist > 1e-10) setHandleAlongTangent(pt, tangent, "left", leftDist);
    if (rightDist > 1e-10)
      setHandleAlongTangent(pt, tangent, "right", rightDist);
  }
}

/**
 * Enforce the aligned constraint: after moving one handle, mirror the
 * opposite handle's direction while preserving its length.
 */
export function enforceAlignedHandle(
  point: CurvePoint,
  movedSide: "left" | "right",
): void {
  const moved = movedSide === "left" ? point.handleLeft : point.handleRight;
  const opposite = movedSide === "left" ? point.handleRight : point.handleLeft;

  const movedDir = v3Sub(moved, point.co);
  const oppositeLen = v3Len(v3Sub(opposite, point.co));
  const movedLen = v3Len(movedDir);

  if (movedLen < 1e-10) return;

  const negDir = v3Scale(movedDir, -oppositeLen / movedLen);
  const newOpposite = v3Add(point.co, negDir);

  if (movedSide === "left") point.handleRight = newOpposite;
  else point.handleLeft = newOpposite;
}

/** Create a default 4-point S-curve in the XZ plane. */
export function createDefaultCurveData(): CurveData {
  const points: CurvePoint[] = [
    {
      co: [-0.5, 0, 0],
      handleLeft: [-0.75, 0, 0],
      handleRight: [-0.25, 0, 0.25],
      handleTypeLeft: "aligned",
      handleTypeRight: "aligned",
      radius: 1,
      tilt: 0,
    },
    {
      co: [-0.17, 0, 0.25],
      handleLeft: [-0.33, 0, 0.25],
      handleRight: [0, 0, 0.25],
      handleTypeLeft: "aligned",
      handleTypeRight: "aligned",
      radius: 1,
      tilt: 0,
    },
    {
      co: [0.17, 0, -0.25],
      handleLeft: [0, 0, -0.25],
      handleRight: [0.33, 0, -0.25],
      handleTypeLeft: "aligned",
      handleTypeRight: "aligned",
      radius: 1,
      tilt: 0,
    },
    {
      co: [0.5, 0, 0],
      handleLeft: [0.25, 0, -0.25],
      handleRight: [0.75, 0, 0],
      handleTypeLeft: "aligned",
      handleTypeRight: "aligned",
      radius: 1,
      tilt: 0,
    },
  ];

  recomputeAlignedHandles(points, false);

  return {
    points,
    resolutionU: 12,
    cyclic: false,
    bevel: { enabled: false, depth: 0.05, resolution: 8, fillCaps: true },
  };
}

/** Set the handle type for a point and recompute its handle positions. */
export function setPointHandleType(
  points: CurvePoint[],
  pointIndex: number,
  type: CurveHandleType,
  cyclic: boolean,
): void {
  const n = points.length;
  const pt = points[pointIndex];
  const prev =
    cyclic || pointIndex > 0 ? points[(pointIndex - 1 + n) % n] : null;
  const next =
    cyclic || pointIndex < n - 1 ? points[(pointIndex + 1) % n] : null;

  pt.handleTypeLeft = type;
  pt.handleTypeRight = type;

  switch (type) {
    case "auto":
      recomputeOneAutoHandle(pt, prev, next, "left");
      recomputeOneAutoHandle(pt, prev, next, "right");
      break;
    case "vector":
      recomputeVectorHandle(pt, prev, next, "left");
      recomputeVectorHandle(pt, prev, next, "right");
      break;
    case "aligned":
      enforceAlignedHandle(pt, "right");
      break;
    case "free":
      break;
  }
}
